import React, { ReactNode } from 'react'

import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'

import classes from './index.module.scss'
import { BaseInput } from '../base-input'

interface ReduxFormInputProps {
  type?: string
  label?: ReactNode
  input: { name: string } & Record<string, unknown>
  meta: { error?: string | string[]; touched?: boolean; submitFailed?: boolean }
  disabled?: boolean
  help?: ReactNode
  id?: string
  showToggle: ReactNode
  placeholder?: string
  autoComplete?: string
  required?: boolean
  dataTestId?: string
  minLength?: number
  componentStyle?: 'one' | 'two'
  noPaste?: boolean

  [key: string]: unknown
}

const ReduxFormInput = ({
  type,
  label,
  input,
  meta: { error, touched, submitFailed },
  disabled,
  help,
  id = input.name,
  showToggle,
  placeholder,
  autoComplete,
  required,
  dataTestId,
  minLength,
  componentStyle,
  noPaste = false,
  ...props
}: ReduxFormInputProps) => {
  const isValid = isNil(error) || isEmpty(error)
  const showError = (submitFailed || touched) && !isValid

  const component = (
    <input
      id={id}
      {...(input as React.InputHTMLAttributes<HTMLInputElement>)}
      {...(props as React.InputHTMLAttributes<HTMLInputElement>)}
      type={type}
      disabled={disabled}
      data-testid={dataTestId}
      placeholder={placeholder}
      autoComplete={autoComplete}
      required={required}
      minLength={minLength}
      aria-describedby={help ? `hinttext-${id}` : undefined}
      className={showError ? classes.inputWithError : classes.inputNormal}
      /**
       * If noPaste prop is passed, prevent pasting into field.
       * Only for email confirmation at the moment
       */
      onPaste={(e) => {
        if (noPaste) {
          e.preventDefault()
          return false
        }
        return true
      }}
    />
  )

  return BaseInput({
    component,
    errors: error ? (Array.isArray(error) ? error : [error]) : undefined,
    touched,
    submitFailed,
    help,
    id,
    showToggle,
    label,
    dataTestId,
    componentStyle,
  })
}

const maxLength =
  (max: number) =>
  (value: string): string | undefined =>
    value && value.length > max
      ? `Must be ${max} characters or less`
      : undefined

const minLength =
  (min: number) =>
  (value: string): string | undefined =>
    value && value.length < min
      ? `Must be ${min} characters or more.`
      : undefined

export const maxLength100 = maxLength(100)
export const maxLength50 = maxLength(50)
export const minLength8 = minLength(8)

export { ReduxFormInput }
export default ReduxFormInput
